/** Settings in the app shell: /settings/:tab (profile, preferences, members, API keys, AI, storage, workspace, credit history, ...) */

import { useState } from 'react'
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Building2, Download, History, Key, LogOut, Network, SlidersHorizontal, Sparkles, HardDrive, User, Users, type LucideIcon } from 'lucide-react'
import { useAuth } from '../hooks/use-auth'
import { useCreditBalance } from '../hooks/use-credits'
import { useAppStore } from '../stores/app-store'
import { apiClient } from '../lib/api-client'
import { signOut } from '../lib/sign-out'
import { cn } from '../lib/utils'
import { useT, type TranslationKey } from '../i18n'
import { MembersTab } from '../components/settings/members-tab'
import { ApiKeysTab } from '../components/settings/api-keys-tab'
import { StorageTab } from '../components/settings/storage-tab'
import { AISettingsTab } from '../components/settings/ai-settings-tab'
import { GraphAISettingsTab } from '../components/settings/graph-ai-settings-tab'
import { ImportTab } from '../components/settings/import-tab'
import { SettingsSectionHeader } from '../components/settings/settings-section-header'
import { TransactionHistory } from '../components/billing/credit-history'
import { CompileModePicker } from '../components/setup/setup-pickers'
import { useCompileMode, useSetCompileMode } from '../hooks/use-compile-mode'
import { LanguageSwitcher, ThemeSwitcher } from '../components/shell/preference-switchers'
import { buttonDangerClass, buttonPrimaryClass, cardClass, errorTextClass, inputClass, labelClass } from '../components/ui/styles'

export const SETTINGS_TABS = ['profile', 'preferences', 'members', 'api-keys', 'ai', 'storage', 'workspace', 'credit-history', 'graph-ai', 'import'] as const
export type SettingsTabId = (typeof SETTINGS_TABS)[number]

const TAB_META: Record<SettingsTabId, { labelKey: TranslationKey; icon: LucideIcon }> = {
  profile: { labelKey: 'settings.tab.profile', icon: User },
  preferences: { labelKey: 'settings.preferences', icon: SlidersHorizontal },
  members: { labelKey: 'settings.members.title', icon: Users },
  'api-keys': { labelKey: 'settings.apiKeys.title', icon: Key },
  ai: { labelKey: 'settings.ai.title', icon: Sparkles },
  storage: { labelKey: 'settings.storage.title', icon: HardDrive },
  workspace: { labelKey: 'settings.tab.workspace', icon: Building2 },
  'credit-history': { labelKey: 'billing.history.settingsTab', icon: History },
  'graph-ai': { labelKey: 'settings.tab.graphAi', icon: Network },
  import: { labelKey: 'settings.tab.import', icon: Download },
}

const isSettingsTab = (value: string | null | undefined): value is SettingsTabId => !!value && (SETTINGS_TABS as readonly string[]).includes(value)

/** /settings and old /settings?tab=x links land on /settings/x (default: profile) */
export function SettingsIndexRedirect() {
  const [params] = useSearchParams()
  const tab = params.get('tab')
  return <Navigate to={`/settings/${isSettingsTab(tab) ? tab : 'profile'}`} replace />
}

export function SettingsPage() {
  const t = useT()
  const { tab } = useParams<{ tab: string }>()
  const isDark = useAppStore((s) => s.theme === 'dark')

  if (!isSettingsTab(tab)) return <Navigate to="/settings/profile" replace />

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-6 md:px-8 md:py-8">
      <h1 className="mb-6 font-display text-2xl font-semibold tracking-tight text-fg">{t('nav.settings')}</h1>
      <div className="flex flex-col gap-6 md:flex-row md:gap-8">
        <nav aria-label={t('nav.settings')} className="-mx-4 overflow-x-auto px-4 md:mx-0 md:w-52 md:shrink-0 md:overflow-visible md:px-0">
          <ul className="flex gap-1 md:flex-col">
            {SETTINGS_TABS.map((id) => {
              const { labelKey, icon: Icon } = TAB_META[id]
              const active = id === tab
              return (
                <li key={id} className="shrink-0">
                  <Link
                    to={`/settings/${id}`}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'flex min-h-9 items-center gap-2 whitespace-nowrap rounded-md px-3 text-sm',
                      active ? 'bg-accent-soft font-medium text-accent-text' : 'text-fg-muted hover:bg-surface-hover hover:text-fg',
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                    {t(labelKey)}
                  </Link>
                </li>
              )
            })}
          </ul>
        </nav>

        <section className="min-w-0 flex-1">
          {tab === 'profile' && <ProfileTab />}
          {tab === 'preferences' && <PreferencesTab />}
          {tab === 'members' && <MembersTab />}
          {tab === 'api-keys' && <ApiKeysTab />}
          {tab === 'ai' && <AISettingsTab />}
          {tab === 'storage' && <StorageTab />}
          {tab === 'workspace' && <WorkspaceTab />}
          {tab === 'credit-history' && <CreditHistoryTab />}
          {/* Not redesigned yet: these legacy tabs pick their colors from the resolved theme */}
          {tab === 'graph-ai' && <GraphAISettingsTab isDark={isDark} />}
          {tab === 'import' && <ImportTab isDark={isDark} />}
        </section>
      </div>
    </div>
  )
}

function ProfileTab() {
  const t = useT()
  const { user } = useAuth()
  const qc = useQueryClient()
  const [name, setName] = useState(user?.name ?? '')

  const updateProfile = useMutation({
    mutationFn: (body: { name: string }) => apiClient.patch('/api/auth/me', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['auth', 'me'] }),
  })


  if (!user) return <p className="text-sm text-fg-subtle">{t('common.loading')}</p>

  const trimmed = name.trim()

  return (
    <div className="space-y-6">
      <SettingsSectionHeader title={t('settings.tab.profile')} description={t('settings.profile.subtitle')} />

      <div className="flex items-center gap-4">
        {user.avatarUrl ? (
          <img src={user.avatarUrl} alt="" className="h-16 w-16 rounded-full" />
        ) : (
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent text-xl font-bold text-accent-fg" aria-hidden="true">
            {user.name.charAt(0).toUpperCase() || '?'}
          </div>
        )}
        <div className="min-w-0">
          <p className="truncate text-lg font-semibold text-fg">{user.name}</p>
          <p className="truncate text-sm text-fg-muted">{user.email}</p>
        </div>
      </div>

      <form
        className={cn(cardClass, 'space-y-4 p-4')}
        onSubmit={(e) => {
          e.preventDefault()
          if (trimmed && trimmed !== user.name) updateProfile.mutate({ name: trimmed })
        }}
      >
        <div className="space-y-1.5">
          <label htmlFor="profile-name" className={labelClass}>
            {t('settings.profile.displayName')}
          </label>
          <input id="profile-name" type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} className={inputClass} />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="profile-email" className={labelClass}>
            {t('settings.members.email')}
          </label>
          <input id="profile-email" type="email" value={user.email} disabled className={inputClass} />
        </div>
        {updateProfile.isError && (
          <p role="alert" className={errorTextClass}>
            {t('settings.saveFailed', { message: updateProfile.error instanceof Error ? updateProfile.error.message : '' })}
          </p>
        )}
        {updateProfile.isSuccess && (
          <p role="status" className="text-sm text-success">
            {t('settings.saved')}
          </p>
        )}
        <button type="submit" disabled={updateProfile.isPending || !trimmed || trimmed === user.name} className={buttonPrimaryClass}>
          {updateProfile.isPending ? t('settings.saving') : t('settings.save')}
        </button>
      </form>

      <div className="space-y-2 rounded-lg border border-danger/30 p-4">
        <h3 className="text-sm font-semibold text-fg">{t('topbar.signOut')}</h3>
        <p className="text-sm text-fg-muted">{t('settings.profile.signOutHint')}</p>
        <button type="button" onClick={() => void signOut()} className={buttonDangerClass}>
          <LogOut className="h-4 w-4" aria-hidden="true" />
          {t('topbar.signOut')}
        </button>
      </div>
    </div>
  )
}

/** Theme and language (keys `theme`, `locale`, via the app-wide preference sync) and the compile mode (`compile_mode`) */
function PreferencesTab() {
  const t = useT()
  const compileMode = useCompileMode()
  const setCompileMode = useSetCompileMode()
  return (
    <div className="space-y-4">
      <SettingsSectionHeader title={t('settings.preferences')} />
      <div className={cn(cardClass, 'divide-y divide-border')}>
        <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className="text-sm font-medium text-fg">{t('theme.label')}</p>
            <p className="mt-0.5 text-xs text-fg-subtle">{t('settings.themeHint')}</p>
          </div>
          <ThemeSwitcher variant="full" className="self-start sm:self-auto" />
        </div>
        <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <p className="text-sm font-medium text-fg">{t('language.label')}</p>
            <p className="mt-0.5 text-xs text-fg-subtle">{t('settings.languageHint')}</p>
          </div>
          <LanguageSwitcher variant="full" className="self-start sm:self-auto" />
        </div>
        <div className="space-y-2 p-4">
          <CompileModePicker value={setCompileMode.isPending && setCompileMode.variables ? setCompileMode.variables : compileMode} onChange={(mode) => setCompileMode.mutate(mode)} />
          {setCompileMode.isError && (
            <p role="alert" className={errorTextClass}>
              {t('compileMode.saveFailed', { message: setCompileMode.error instanceof Error ? setCompileMode.error.message : '' })}
            </p>
          )}
          {setCompileMode.isSuccess && (
            <p role="status" className="text-sm text-success">
              {t('compileMode.saved')}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

/** Settings › Credit history: the same ledger as /billing */
function CreditHistoryTab() {
  const t = useT()
  return (
    <div className="space-y-4">
      <SettingsSectionHeader title={t('billing.history.settingsTab')} description={t('billing.history.settingsDescription')} />
      <TransactionHistory />
    </div>
  )
}

/** Credit balance with a link to billing (billing itself is redesigned in milestone 4b) */
function WorkspaceTab() {
  const t = useT()
  const locale = useAppStore((s) => s.locale)
  const { data: balance, isLoading } = useCreditBalance()
  const total = balance?.totalCredits ?? 0

  return (
    <div className="space-y-4">
      <SettingsSectionHeader title={t('settings.tab.workspace')} />
      <div className={cn(cardClass, 'flex flex-col gap-3 p-4 sm:flex-row sm:items-end sm:justify-between')}>
        <div>
          <p className="text-xs text-fg-subtle">{t('settings.workspace.credits')}</p>
          <p className="mt-0.5 text-lg font-semibold tabular-nums text-fg">
            {isLoading ? '…' : t('settings.workspace.creditCount', { count: total.toLocaleString(locale) })}
          </p>
        </div>
        <Link to="/billing" className={buttonPrimaryClass}>
          {t('settings.workspace.manageBilling')}
        </Link>
      </div>
    </div>
  )
}
